import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Amount } from "@/components/common/Amount";
import { PROJECT_STATUSES, projectStatusTone } from "@/lib/projects";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { ProgressBar } from "@/features/projects/ProgressBar";
import { ProjectDialog } from "@/features/projects/ProjectDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 25;

export default function ProjectsPage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const get = (k, d = "") => params.get(k) || d;
  const patch = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (!v || v === "all") next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const page = Number(get("page", "1"));
  const alerta = get("alerta") === "1";

  const { data: users } = useQuery({
    queryKey: ["users-options"],
    enabled: can("users", "read"),
    queryFn: async () => (await api.get("/users")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["projects", { q: get("q"), estado: get("estado"), responsable_user_id: get("responsable_user_id"), alerta, page, brandParam }],
    queryFn: async () => (await api.get("/projects", {
      params: {
        q: get("q") || undefined, estado: get("estado") || undefined,
        responsable_user_id: get("responsable_user_id") || undefined,
        alerta: alerta || undefined, brand: brandParam, page, page_size: PAGE_SIZE,
      },
    })).data,
  });

  const rows = data?.items || [];
  const showMoney = rows.some((r) => r.monto_contratado_cents !== undefined);
  const activeFilters = ["q", "estado", "responsable_user_id", "alerta"].filter((k) => params.get(k));

  const columns = [
    { key: "code", label: t("projects.code"), width: 128, render: (r) => <span className="font-mono text-[12.5px] text-ink">{r.code}</span> },
    {
      key: "direccion_obra", label: t("projects.work"),
      render: (r) => (
        <p className="truncate text-[13px] text-ink">
          {r.direccion_obra?.calle}
          <span className="text-ink-tertiary"> · {r.direccion_obra?.ciudad}</span>
        </p>
      ),
    },
    {
      key: "cliente_nombre", label: t("projects.client"), width: 160,
      render: (r) => <span className="truncate text-[12.5px] text-ink">{r.cliente_tipo === "company" ? r.company_nombre || t("projects.client_type.company") : r.cliente_nombre}</span>,
    },
    { key: "brand", label: t("projects.brand"), width: 84, render: (r) => <span className="text-[12.5px] uppercase text-ink-secondary">{r.brand}</span> },
    { key: "responsable_nombre", label: t("projects.responsible"), width: 140, render: (r) => r.responsable_nombre || <span className="text-ink-tertiary">—</span> },
    {
      key: "avance_pct", label: t("projects.progress_label"), width: 120,
      render: (r) => <ProgressBar pct={r.avance_pct || 0} />,
    },
  ];
  if (showMoney) {
    columns.push({
      key: "monto_contratado_cents", label: t("projects.contracted"), width: 110, align: "right",
      render: (r) => <Amount cents={r.monto_contratado_cents} className="text-[12.5px]" />,
    });
    columns.push({
      key: "cobrado_cents", label: t("projects.collected"), width: 110, align: "right",
      render: (r) => <Amount cents={r.cobrado_cents} className="text-[12.5px]" />,
    });
  }
  columns.push({
    key: "estado", label: t("app.status"), width: 116,
    render: (r) => <StatusChip tone={projectStatusTone(r.estado)} label={t(`projects.status.${r.estado}`)} testId={`project-status-${r.code}`} />,
  });

  return (
    <>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
        testId="projects-header"
        actions={
          can("projects", "create") && (
            <Button data-testid="new-project-button" onClick={() => setDialogOpen(true)} className="h-8">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t("projects.new")}
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 divide-x divide-line border-b border-line bg-paper px-6 py-2.5">
        <div className="relative pr-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="projects-search-input"
            key={get("q") || "empty"}
            defaultValue={get("q")}
            onKeyDown={(e) => e.key === "Enter" && patch({ q: e.currentTarget.value })}
            onBlur={(e) => patch({ q: e.currentTarget.value })}
            placeholder={t("projects.search")}
            className="h-8 w-[260px] pl-8"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-0 pr-3">
          <Select value={get("estado", "all")} onValueChange={(v) => patch({ estado: v })}>
            <SelectTrigger data-testid="projects-status-filter" className="h-8 w-[150px]">
              <SelectValue placeholder={t("app.status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("app.status")}: {t("app.all")}</SelectItem>
              {PROJECT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t(`projects.status.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can("users", "read") && (
            <Select value={get("responsable_user_id", "all")} onValueChange={(v) => patch({ responsable_user_id: v })}>
              <SelectTrigger data-testid="projects-responsible-filter" className="h-8 w-[160px]">
                <SelectValue placeholder={t("projects.responsible")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("projects.responsible")}: {t("app.all")}</SelectItem>
                {(users?.items || []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <label className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-secondary">
            <Checkbox data-testid="projects-alert-filter" checked={alerta} onCheckedChange={(v) => patch({ alerta: v ? "1" : "" })} />
            {t("projects.alerts_only")}
          </label>
        </div>
        {!!activeFilters.length && (
          <Button variant="ghost" data-testid="projects-clear-filters" onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="h-8 text-ink-secondary hover:text-ink">
            <X className="mr-1 h-3.5 w-3.5" /> {t("projects.clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-paper" data-testid="projects-page">
        <DataTable
          testId="projects-table"
          columns={columns}
          rows={rows}
          total={data?.total || 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => patch({ page: String(p) })}
          loading={isLoading}
          onRowClick={(r) => navigate(`/proyectos/${r.id}`)}
          empty={
            <EmptyState
              testId="projects-empty"
              title={t("projects.empty_title")}
              description={t("projects.empty_desc")}
              action={
                can("projects", "create") && (
                  <Button data-testid="projects-empty-new" onClick={() => setDialogOpen(true)} className="h-8">
                    {t("projects.new")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <ProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
